import type { ComponentPropsWithoutRef, MouseEvent } from "react";

import dayjs, { type ConfigType } from "@eisox/dayjs";
import { cn, ToggleGroup } from "@eisox/design-system";
import { HistoryIcon, PlayIcon, UpdateIcon } from "@eisox/icons";
import { usePendingAction } from "@eisox/tools";
import { createContext } from "@radix-ui/react-context";
import { useControllableState } from "@radix-ui/react-use-controllable-state";

import { m } from "~/paraglide/messages";

enum Mode {
  Live = "live",
  History = "history",
}

interface LiveHistoryToggleContextValue {
  mode: Mode;
  setMode: (mode: Mode) => void;
}

const [LiveHistoryToggleProvider, useLiveHistoryToggleContext] =
  createContext<LiveHistoryToggleContextValue>("LiveHistoryToggle");

interface RootProps extends ComponentPropsWithoutRef<"div"> {
  mode?: Mode;
  onModeChange?: (mode: Mode) => void;
  defaultMode?: Mode;
}

const Root: React.FC<RootProps> = ({ mode: modeProp, onModeChange, defaultMode = Mode.Live, ...props }) => {
  const [mode, setMode] = useControllableState({
    prop: modeProp,
    onChange: onModeChange,
    defaultProp: defaultMode,
  });

  return (
    <LiveHistoryToggleProvider mode={mode} setMode={setMode}>
      <div {...props} />
    </LiveHistoryToggleProvider>
  );
};

Root.displayName = "LiveHistoryToggle.Root";

const TABS_NAME = "LiveHistoryToggle.Tabs";

interface TabsProps extends Omit<ComponentPropsWithoutRef<typeof ToggleGroup.Root>, "value" | "onValueChange"> {
  historyDisabled?: boolean;
}

const Tabs: React.FC<TabsProps> = ({ className, historyDisabled = false, ...props }) => {
  const { mode, setMode } = useLiveHistoryToggleContext(TABS_NAME);

  return (
    <ToggleGroup.Root
      {...props}
      className={cn("md:bg-primary-gray bg-primary-white", className)}
      value={mode}
      onValueChange={value => setMode(value as Mode)}
    >
      <ToggleGroup.Item
        className="md:text-base [&>span>span]:gap-0 [&>span>span]:md:gap-1 text-[0px]"
        value={Mode.History}
        disabled={historyDisabled}
      >
        <HistoryIcon />
        {m.history()}
      </ToggleGroup.Item>
      <ToggleGroup.Item
        className="md:text-base [&>span>span]:gap-0 [&>span>span]:md:gap-1 text-[0px]"
        value={Mode.Live}
      >
        <PlayIcon />
        {m.live()}
      </ToggleGroup.Item>
    </ToggleGroup.Root>
  );
};

Tabs.displayName = TABS_NAME;

interface TimestampProps extends ComponentPropsWithoutRef<"p"> {
  date?: ConfigType;
  format?: string;
}

const Timestamp: React.FC<TimestampProps> = ({ date, format = "L - LTS", className, ...props }) => (
  <p {...props} className={cn("font-light text-base italic", className)}>
    {dayjs(date).isValid() ? dayjs(date).format(format) : "-"}
  </p>
);

Timestamp.displayName = "LiveHistoryToggle.Timestamp";

const RELOAD_NAME = "LiveHistoryToggle.Reload";

interface ReloadProps extends Omit<ComponentPropsWithoutRef<"button">, "children" | "onClick"> {
  onReload?: (event: MouseEvent<HTMLButtonElement>) => unknown;
  isPending?: boolean;
  size?: number;
}

const Reload: React.FC<ReloadProps> = ({ onReload, isPending = false, size = 21, className, style, ...props }) => {
  const { mode } = useLiveHistoryToggleContext(RELOAD_NAME);
  const { isPending: isReloading, run } = usePendingAction(onReload);

  if (mode !== Mode.Live || !onReload) return null;

  const pending = isPending || isReloading;

  return (
    <button
      {...props}
      type="button"
      aria-label={m.refresh()}
      aria-busy={pending}
      disabled={pending}
      onClick={run}
      style={{ width: size, height: size, ...style }}
      className={cn(
        "flex shrink-0 cursor-pointer items-center justify-center rounded-full bg-primary-gray text-primary-dark-blue enabled:hover:bg-primary-darker-gray enabled:active:bg-primary-dark-gray enabled:active:text-primary-white disabled:cursor-progress [&>svg]:h-[60%] [&>svg]:w-[60%]",
        className,
      )}
    >
      <UpdateIcon className={cn(pending && "animate-spin")} />
    </button>
  );
};

Reload.displayName = RELOAD_NAME;

export { Mode, Reload, Root, Tabs, Timestamp };
export type { ReloadProps, RootProps, TabsProps, TimestampProps };
